import { useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  FormControlLabel,
  Checkbox,
  Grid,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Button from "@/components/button";
import { useAuthContext } from "@/context/AuthContext";
import { useCartContext } from "@/context/CartContext";

import { MAX_ITEM_COUNT, MAX_PURCHASE_PRICE, ERRORS } from "@/const";
import { UserCard } from "@/components/userCard";
import convertToPoints from "@/utils/convertToPoints";
import CartItems from "./CartItems";

export const ShopCheckout = () => {
  const theme = useTheme();
  const { user } = useAuthContext();
  const [nonRefund, setNonRefund] = useState(false);
  const [termsRead, setTermsRead] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [missingInfo, setMissingInfo] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();
  const { cart, totalCostPoints, totalItemCount, submitForPurchase } =
    useCartContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const hasZincProduct = Object.keys(cart?.items?.zincItems || {}).length > 0;

  const pointsDifference = Math.round((user.points || 0) - totalCostPoints);
  const notEnoughPoints = pointsDifference < 0;
  const tooManyProducts = MAX_ITEM_COUNT < totalItemCount;
  const cartExceedsMaxPrice =
    convertToPoints(MAX_PURCHASE_PRICE) < totalCostPoints; //???
  const purchaseRestricted =
    notEnoughPoints || tooManyProducts || cartExceedsMaxPrice;

  const handleMissingInfoUpdate = (info: any) => {
    setMissingInfo(info);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const result = await submitForPurchase();
    if (result?.id) {
      navigate(`/shop/history/${result.id}`);
    } else {
      setSubmitting(false);
    }
  };

  return totalItemCount ? (
    <Box data-cypress="shop-checkout-page" sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <UserCard
            productPresent={hasZincProduct}
            onMissingInfoUpdate={handleMissingInfoUpdate}
            setEditMode={setEditMode}
            editMode={editMode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <CartItems />
          <Typography data-cypress="checkout-total" align="right">
            Total: {totalCostPoints} Points
          </Typography>
          {notEnoughPoints && (
            <Typography align="right" color="error">
              Your purchase amount exceeds your points balance by{" "}
              {Math.round(pointsDifference * -1)}
            </Typography>
          )}
          {tooManyProducts && (
            <Typography align="right" color="error">
              {ERRORS.purchase.maxItemCountError}
            </Typography>
          )}
          {cartExceedsMaxPrice && (
            <Typography align="right" color="error">
              {ERRORS.purchase.maxPurchasePriceError}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  checked={nonRefund}
                  onChange={() => setNonRefund(!nonRefund)}
                  disabled={submitting || purchaseRestricted}
                />
              }
              label="I understand my order is non-refundable and non-cancellable"
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  checked={termsRead}
                  onChange={() => setTermsRead(!termsRead)}
                  disabled={submitting || purchaseRestricted}
                />
              }
              label={
                <>
                  I have read and accept the{" "}
                  <Link to={`/shop/terms`}>Terms and Conditions</Link>
                </>
              }
            />
            <Button
              data-cypress="submit-purchase-button"
              onClick={handleSubmit}
              variant="contained"
              disabled={
                !nonRefund ||
                !termsRead ||
                submitting ||
                purchaseRestricted ||
                (hasZincProduct && missingInfo.length > 0) ||
                editMode
              }
              fullWidth
              sx={{ mt: 2 }}
            >
              {submitting ? "Processing..." : "Submit Purchase"}
            </Button>
            {hasZincProduct && missingInfo.length > 0 && (
              <Typography mt={1} color="error">
                {" "}
                Missing address information. Please fill out your address
                information{" "}
                {isMobile
                  ? "at the top of this page to submit your purchase."
                  : "on the left to submit your purchase."}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  ) : (
    <Box>
      <Typography data-cypress="no-items-in-cart">
        You have no items in your cart
      </Typography>
      <Link to={`/shop`} data-cypress="back-to-shop-button">
        Back to Shop
      </Link>
    </Box>
  );
};
